/**
 * PAGE HEADER COMPONENT
 *
 * Reusable header for pages with consistent glassmorphic styling.
 * Features:
 * - Glassmorphic background (glass-card)
 * - Centered text with icon to the left
 * - Optional subtitle
 * - Optional action buttons (right side) or children content
 *
 * @example
 * <PageHeader
 *   icon={Calendar}
 *   title="Events Calendar"
 *   subtitle="Never miss a sale, restock, or release again!"
 * />
 *
 * // With action button:
 * <PageHeader
 *   icon={Palette}
 *   title="My Creations"
 *   subtitle="Track your manis and list items for sale"
 *   actions={<button>Add Creation</button>}
 * />
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export interface PageHeaderProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional additional classes for the container */
  className?: string;
  /** Optional action buttons/elements (displayed on right side) */
  actions?: ReactNode;
  /** Optional children content (displayed below title, e.g., stats) */
  children?: ReactNode;
}

export default function PageHeader({
  icon: Icon,
  title,
  subtitle,
  className = '',
  actions,
  children,
}: PageHeaderProps) {
  return (
    <div className={`glass-card p-6 ${className}`}>
      {/* Header row with optional actions */}
      <div className={actions ? 'flex flex-col md:flex-row md:items-center md:justify-between gap-4' : 'text-center'}>
        <div className={actions ? '' : ''}>
          <h1 className={`text-3xl font-bold text-glass mb-2 font-heading flex items-center gap-3 ${actions ? '' : 'justify-center'}`}>
            <Icon className="icon-lg text-current" />
            {title}
          </h1>
          {subtitle && (
            <p className={`text-glass-secondary font-body ${actions ? '' : ''}`}>
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
      {/* Optional children content below header */}
      {children}
    </div>
  );
}
